// De plan-modus van de Trends-tab (Z14 = A, owner 26-10): een planbalk, grijs wat al gekozen is,
// het filter "nog niet gekozen" en "+ Toevoegen aan 1.3".
// Sinds "Op weg naar live" (04-11, stuk 2) heeft 1.3 de stappen Onderwerpen en Kaarten en geen stap
// "Zelf toevoegen" meer: niets in het plan roept `beginPlanModus` nog aan, dus de modus staat in de
// app altijd uit. De Trends-tab en de kaarten lezen hem nog (en hun tests zetten hem zelf aan);
// weghalen kan pas samen met die onderdelen.
//
// Eén klein gedeeld stuk stand: in het geheugen, met een kopie in
// sessionStorage (blijft bij herladen in dit tabblad, niet daarbuiten) en een
// abonnement voor React (useSyncExternalStore).

import { useSyncExternalStore } from "react";

import type { TrendlaagOntwikkeling, TrendlaagTrend } from "../../lib/api";
import { type Filters, LEGE_FILTERS, past } from "./lijst";

/** Hooguit zoveel zelf toegevoegd; gelijk aan MAX_ZELF in plan/variantA13.ts (de test bewaakt dat). */
export const MAX_ZELF_PLAN = 10;

export type Startpunt = "regels" | "werkvloer" | "opkomend" | "top";

/** De vier voorgestelde startpunten, in deze volgorde op het scherm. */
export const STARTPUNTEN: { s: Startpunt; naam: string; uitleg: string }[] = [
  { s: "regels", naam: "Regels met een datum", uitleg: "Alleen: Wet" },
  { s: "werkvloer", naam: "Werkvloer in jullie top-onderwerpen", uitleg: "Onderwerp: jullie top · Soort: Werkvloer" },
  { s: "opkomend", naam: "Opkomend", uitleg: "Alleen: Opkomend" },
  { s: "top", naam: "In jullie top-onderwerpen, nog niet gekozen", uitleg: "Onderwerp: jullie top" },
];
export const naamVan = (s: Startpunt | null | undefined): string | null => STARTPUNTEN.find((x) => x.s === s)?.naam ?? null;

/** De filters van de Trends-tab die bij een startpunt horen (zonder "nog niet gekozen"). */
export function filtersVoor(s: Startpunt, top: readonly string[]): Filters {
  switch (s) {
    case "regels":
      return { ...LEGE_FILTERS, kenmerk: ["wet"] };
    case "werkvloer":
      return { ...LEGE_FILTERS, onderwerp: [...top], soort: ["werkvloer"] };
    case "opkomend":
      return { ...LEGE_FILTERS, kenmerk: ["opkomend"] };
    case "top":
      return { ...LEGE_FILTERS, onderwerp: [...top] };
  }
}

/** Staan de filters precies op dit startpunt? Dan toont de tab één pil "Voorstel: …". */
export function zijnStartFilters(f: Filters, s: Startpunt | null, top: readonly string[]): boolean {
  if (!s) return false;
  const v = filtersVoor(s, top);
  const zelfde = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));
  return zelfde(f.onderwerp, v.onderwerp) && zelfde(f.soort, v.soort) && zelfde(f.beweging, v.beweging) && zelfde(f.kenmerk, v.kenmerk);
}

/** "Nog niet gekozen": de trend heeft minstens één ontwikkeling die niet in Swipen gekozen is.
 *  Zelf toegevoegde tellen hier niet mee, zodat een trend niet verdwijnt terwijl je toevoegt. */
export const nogNietGekozen = (t: TrendlaagTrend, gekozen: ReadonlySet<string>): boolean =>
  t.ontwikkelingen.some((o) => !gekozen.has(o.id));

/** Belooft dit startpunt deze ontwikkeling? (owner 26-10: het grote getal telt
 *  wat het startpunt belooft, niet alle vrije ontwikkelingen van de trends.)
 *  Regels: een regel met een datum. Werkvloer: een werkvloer-ontwikkeling in
 *  jullie top. Opkomend: zelf opkomend, of in een opkomende trend. Top: elke
 *  ontwikkeling in jullie top. */
export function pastBijStartpunt(t: TrendlaagTrend, o: TrendlaagOntwikkeling, s: Startpunt, top: readonly string[]): boolean {
  switch (s) {
    case "regels":
      return o.regelMetDatum;
    case "werkvloer":
      return top.includes(t.onderwerp) && o.soort === "werkvloer";
    case "opkomend":
      return o.opkomend || t.opkomend;
    case "top":
      return top.includes(t.onderwerp);
  }
}

/** Heeft de trend bij dit startpunt nog iets te kiezen? Zelf toegevoegde tellen
 *  hier als nog te kiezen, zodat een trend niet verdwijnt terwijl je toevoegt. */
export const heeftVrijBijStartpunt = (t: TrendlaagTrend, s: Startpunt, top: readonly string[], gekozen: ReadonlySet<string>): boolean =>
  t.ontwikkelingen.some((o) => !gekozen.has(o.id) && pastBijStartpunt(t, o, s, top));

/** Wat de tab bij dit startpunt laat zien: zoveel trends, met zoveel ontwikkelingen
 *  die het startpunt belooft en die je nog kunt toevoegen (niet gekozen, niet al
 *  zelf toegevoegd). De Trends-tab in plan-modus toont hetzelfde getal. */
export function telStartpunt(
  trends: readonly TrendlaagTrend[],
  s: Startpunt,
  top: readonly string[],
  gekozen: ReadonlySet<string>,
  zelf: readonly string[],
): { trends: number; ontwikkelingen: number } {
  // Zonder top-onderwerpen (nog geen punten verdeeld) zou "jullie top" alles zijn.
  if ((s === "werkvloer" || s === "top") && top.length === 0) return { trends: 0, ontwikkelingen: 0 };
  const f = filtersVoor(s, top);
  const z = new Set(zelf);
  const raak = trends.filter((t) => past(t, f) && heeftVrijBijStartpunt(t, s, top, gekozen));
  return {
    trends: raak.length,
    ontwikkelingen: raak.reduce(
      (n, t) => n + t.ontwikkelingen.filter((o) => !gekozen.has(o.id) && !z.has(o.id) && pastBijStartpunt(t, o, s, top)).length,
      0,
    ),
  };
}

// ── De stand ────────────────────────────────────────────────────────────────

export type PlanModus = {
  /** De Trends-tab staat in plan-modus (je bent onderweg vanuit 1.3). */
  actief: boolean;
  /** Net teruggekomen via "Terug naar het plan": 1.3 neemt de lijst over. */
  terug: boolean;
  startpunt: Startpunt | null;
  /** De top-onderwerpen uit de Munten. */
  top: string[];
  /** In Swipen gekozen (vinkjes en regels met datum): grijs, niet toe te voegen. */
  gekozen: string[];
  /** Zelf toegevoegd, hooguit 10. */
  zelf: string[];
  /** Per zelf toegevoegde ontwikkeling: vanuit welk startpunt. */
  via: Record<string, Startpunt | null>;
};

const SLEUTEL = "summa-plan-13a-zoeken";
const LEEG: PlanModus = { actief: false, terug: false, startpunt: null, top: [], gekozen: [], zelf: [], via: {} };

function lees(): PlanModus {
  try {
    const s = JSON.parse(sessionStorage.getItem(SLEUTEL) ?? "null") as Partial<PlanModus> | null;
    return s ? { ...LEEG, ...s } : LEEG;
  } catch {
    return LEEG;
  }
}

let stand: PlanModus = lees();
const luisteraars = new Set<() => void>();

function zet(deel: Partial<PlanModus>): void {
  stand = { ...stand, ...deel };
  try {
    sessionStorage.setItem(SLEUTEL, JSON.stringify(stand));
  } catch {
    /* geen opslag: de stand leeft dan alleen in dit scherm */
  }
  luisteraars.forEach((f) => f());
}

export const leesPlanModus = (): PlanModus => stand;

export function abonneerPlanModus(f: () => void): () => void {
  luisteraars.add(f);
  return () => luisteraars.delete(f);
}

export function usePlanModus(): PlanModus {
  return useSyncExternalStore(abonneerPlanModus, leesPlanModus, leesPlanModus);
}

/** Vanuit 1.3: de Trends-tab gaat in plan-modus, met dit startpunt en de keuze van dit moment. */
export function beginPlanModus(p: { startpunt: Startpunt | null; top: readonly string[]; gekozen: readonly string[]; zelf: readonly string[] }): void {
  const zelf = p.zelf.slice(0, MAX_ZELF_PLAN);
  const via = Object.fromEntries(zelf.map((id) => [id, stand.via[id] ?? null]));
  zet({ actief: true, terug: false, startpunt: p.startpunt, top: [...p.top], gekozen: [...p.gekozen], zelf, via });
}

export type Status = "gekozen" | "toegevoegd" | "vrij" | "vol";

export function statusVan(p: PlanModus, id: string): Status {
  if (p.gekozen.includes(id)) return "gekozen";
  if (p.zelf.includes(id)) return "toegevoegd";
  return p.zelf.length >= MAX_ZELF_PLAN ? "vol" : "vrij";
}

/** Toevoegen aan 1.3; false als hij al gekozen is of de 10 vol zijn. */
export function voegToe(id: string): boolean {
  if (statusVan(stand, id) !== "vrij") return false;
  zet({ zelf: [...stand.zelf, id], via: { ...stand.via, [id]: stand.startpunt } });
  return true;
}

export function haalWeg(id: string): void {
  if (!stand.zelf.includes(id)) return;
  const via = { ...stand.via };
  delete via[id];
  zet({ zelf: stand.zelf.filter((x) => x !== id), via });
}

/** De balk "Terug naar het plan": de modus gaat uit, 1.3 neemt de lijst over. */
export const terugNaarPlan = (): void => zet({ actief: false, terug: true });

/** 1.3 heeft de lijst overgenomen (of je ging verder naar Rangschikken). */
export const stopPlanModus = (): void => zet({ actief: false, terug: false });

/** Alleen voor tests: de stand leeg. */
export function leegPlanModus(): void {
  try {
    sessionStorage.removeItem(SLEUTEL);
  } catch {
    /* niets */
  }
  stand = LEEG;
  luisteraars.forEach((f) => f());
}
